import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody } from 'reactstrap';
import axios from 'axios';
import { api } from '../../../config';
import notify from '../../../utils/notify';
import { confirmDelete } from '../../../utils/confirm';
import { unwrapApiItems } from '../../../shared/api/unwrap';
import {
  mapSupportDocumentItem,
  mapCounterDocumentItem,
  isOtherSupportDocument,
  mergeDocumentsWithCounters,
} from '../../../shared/offer-domain/document';
import { isOfferDetailReadOnly } from '../utils/offerEditAccess';
import ActionButton from '../../../Components/Common/ActionButton';
import { SkeletonCard } from '../../../Components/Common/Skeleton';
import useAsyncAction from '../../../Components/Hooks/useAsyncAction';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  FileField,
  FormActions,
  FormGrid,
  RequiredNote,
  TextField,
  focusField,
} from '../../../Components/Form';
import { formatDate } from '../../../utils/format';

const SupportDocumentTab = ({
  ids,
  token,
  formMain = {},
  handleInvitationAccept,
  handleSaveAsDraft,
  fetchDataInvitationValidation,
  ensureClausesConfirmed,
}) => {
  const [procurementDocuments, setProcurementDocuments] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [fileCategories, setFileCategories] = useState([]);
  const [uploadModal, setUploadModal] = useState({
    isOpen: false,
    document: null,
  });
  const [customUploadModal, setCustomUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [customFile, setCustomFile] = useState(null);
  const [customDocumentTitle, setCustomDocumentTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFileError, setUploadFileError] = useState('');
  const [customErrors, setCustomErrors] = useState({});

  const uploadFileRef = useRef(null);
  const customFileRef = useRef(null);

  const isReadOnly = isOfferDetailReadOnly({
    statusOfferingWordingFlag: formMain.statusOfferingWordingFlag,
    statusOfferingWording: formMain.statusOfferingWording,
    submissionStatus: formMain.submissionStatus,
  });

  const validateAndExecute = async (
    actionFn,
    { confirmClauses = false } = {}
  ) => {
    let isValid = false;
    try {
      isValid = await fetchDataInvitationValidation(ids, token);
    } catch {
      return;
    }

    if (isValid) {
      notify.warning('This offer has already been submitted.');
      return;
    }

    if (confirmClauses && ensureClausesConfirmed && !ensureClausesConfirmed()) {
      return;
    }

    try {
      await actionFn();
    } catch (error) {
      void error;
    }
  };

  const [handleExecuteDraft, isExecutingDraft] = useAsyncAction(() =>
    validateAndExecute(handleSaveAsDraft)
  );
  const [handleExecuteOffer, isExecutingOffer] = useAsyncAction(() =>
    validateAndExecute(handleInvitationAccept, { confirmClauses: true })
  );

  useEffect(() => {
    if (ids) {
      fetchDocumentUpload(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  useEffect(() => {
    fetchFileCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFileCategories = async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/document-upload/getall-master-procurement-document`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.documentName,
      }));
      setFileCategories(items);
    } catch {
      void 0;
    }
  };

  const unwrapApiDocumentId = payload =>
    payload?.id ??
    payload?.Id ??
    payload?.data?.id ??
    payload?.data?.Id ??
    payload?.procurementDocumentId ??
    payload?.ProcurementDocumentId ??
    null;

  const customCategory = fileCategories.find(cat =>
    /upload custom|custom/i.test(cat.label || '')
  );

  const applySupportDocuments = (items, counters = []) => {
    const normalized = items.map(item => ({
      ...item,
      updated: item.lastUpdated || item.createdDate,
    }));

    const merged = mergeDocumentsWithCounters(normalized, counters);

    setProcurementDocuments(
      merged.filter(item => !isOtherSupportDocument(item, customCategory))
    );
    setOtherDocuments(
      merged.filter(item => isOtherSupportDocument(item, customCategory))
    );
    setDocsLoaded(true);
  };

  const fetchDocumentCounters = async procurementId => {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement-document-counter/by-procurement/${procurementId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return unwrapApiItems(result).map(mapCounterDocumentItem);
    } catch (error) {
      return [];
    }
  };

  const fetchDocumentUpload = async procurementId => {
    try {
      const [docResponse, counters] = await Promise.all([
        fetch(
          `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${procurementId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetchDocumentCounters(procurementId),
      ]);

      const result = await docResponse.json();
      const items = unwrapApiItems(result)
        .map(mapSupportDocumentItem)
        .filter(item => item.isShowToDMUT === true || item.isShowToDMUT === 1);

      applySupportDocuments(items, counters);
    } catch {
      setDocsLoaded(true);
    }
  };

  const handleConfirmDelFileUpload = async (
    id = null,
    isCustom = false,
    name = undefined
  ) => {
    if (isReadOnly) return undefined;
    const confirmed = await confirmDelete('document', name, {
      message:
        'The uploaded document will be permanently deleted. This cannot be undone.',
    });
    if (!confirmed) return undefined;
    return handleDelFileUpload(id, isCustom);
  };

  const handleDelFileUpload = async (id, isCustom = false) => {
    if (!id) {
      notify.error(
        'The document could not be identified. Refresh the page and try again.'
      );
      return;
    }

    try {
      setUploading(true);
      await axios.delete(`${api.API_URL}/procurement-document/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Data deleted successfully.');

      if (isCustom) {
        setOtherDocuments(prev => prev.filter(doc => doc.id !== id));
      } else {
        setProcurementDocuments(prev => prev.filter(doc => doc.id !== id));
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        notify.error(
          'The document was not found on the server. Refresh the page to see the latest list.'
        );
      } else {
        notify.apiError(error, 'delete the document');
      }
    } finally {
      setUploading(false);
      await fetchDocumentUpload(ids);
    }
  };

  const clearCounterFromState = counterId => {
    setProcurementDocuments(prev =>
      prev.map(doc => {
        if (String(doc.counterId) !== String(counterId)) return doc;
        return {
          ...doc,
          counterId: null,
          hasCounter: false,
          actualUrl: null,
          isMasterDocument: true,
          updated: doc.lastUpdated || doc.createdDate || null,
        };
      })
    );
  };

  const handleConfirmDelCounter = async (counterId, name = undefined) => {
    if (isReadOnly) return undefined;
    if (!counterId) {
      notify.error(
        'The counter document could not be identified. Refresh the page and try again.'
      );
      return undefined;
    }

    const confirmed = await confirmDelete('counter document', name, {
      message:
        'Your counter document will be permanently deleted and the master document will apply again. This cannot be undone.',
    });
    if (!confirmed) return undefined;
    return handleDelCounter(counterId);
  };

  const handleDelCounter = async counterId => {
    if (!counterId) {
      notify.error(
        'The counter document could not be identified. Refresh the page and try again.'
      );
      return;
    }

    try {
      setUploading(true);
      const response = await axios.delete(
        `${api.API_URL}/procurement-document-counter/${counterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const payload = response?.data;
      const isFailed =
        payload?.succeeded === false ||
        payload?.Succeeded === false ||
        payload?.success === false ||
        payload?.Success === false;

      if (isFailed) {
        throw new Error(
          payload?.message || payload?.Message || 'Delete counter failed'
        );
      }

      clearCounterFromState(counterId);
      notify.success('Counter document deleted successfully.');
      await fetchDocumentUpload(ids);
      // Ensure UI stays cleared if list API is briefly stale
      clearCounterFromState(counterId);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        notify.error(
          'The counter document was not found on the server. Refresh the page to see the latest list.'
        );
      } else {
        notify.apiError(error, 'delete the counter document');
      }
      await fetchDocumentUpload(ids);
    } finally {
      setUploading(false);
    }
  };

  const handleDirectOpenNewTab = url => {
    if (!url) {
      notify.error(
        'The file is not available. Refresh the page and try again, or upload it again.'
      );
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUploadModal = () => {
    setUploadModal({ isOpen: false, document: null });
    setUploadFile(null);
    setUploadFileError('');
    if (uploadFileRef.current) {
      uploadFileRef.current.value = null;
    }
  };

  const resetCustomModal = () => {
    setCustomUploadModal(false);
    setCustomFile(null);
    setCustomDocumentTitle('');
    setCustomErrors({});
    if (customFileRef.current) {
      customFileRef.current.value = null;
    }
  };

  const handleCounterDocument = async () => {
    if (isReadOnly) return;
    if (!uploadFile) {
      setUploadFileError('File is required');
      focusField('supportFile');
      return;
    }

    const doc = uploadModal.document;
    if (!doc) return;

    if (!doc.id) {
      notify.error(
        'The document could not be identified. Refresh the page and try again.'
      );
      return;
    }

    if (!doc.documentUploadId) {
      notify.warning(
        'The document category is missing. Select a category and try again.'
      );
      return;
    }

    const isUpdate = Boolean(doc.counterId || doc.hasCounter);

    if (isUpdate && !doc.counterId) {
      notify.error(
        'The counter document could not be identified. Refresh the page and try again.'
      );
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('ProcurementDocumentId', doc.id);
      formData.append('DocumentUploadId', doc.documentUploadId);
      formData.append(
        'FileName',
        doc.documentName || doc.customFileName || uploadFile.name
      );
      formData.append('FileAttachment', uploadFile);
      formData.append('IsShowToDMUT', 'true');

      const endpoint = isUpdate
        ? `${api.API_URL}/procurement-document-counter/${doc.counterId}`
        : `${api.API_URL}/procurement-document-counter`;

      await axios({
        method: isUpdate ? 'put' : 'post',
        url: endpoint,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      notify.success(
        isUpdate
          ? 'Counter document updated successfully.'
          : 'Counter document uploaded successfully.'
      );
      resetUploadModal();
      await fetchDocumentUpload(ids);
    } catch (error) {
      notify.apiError(
        error,
        `${isUpdate ? 'update' : 'upload'} counter document`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCustomUpload = async () => {
    if (isReadOnly) return;
    const nextErrors = {};
    if (!customDocumentTitle.trim()) {
      nextErrors.customDocumentTitle = 'Document title is required';
    }
    if (!customFile) nextErrors.customFile = 'File is required';
    setCustomErrors(nextErrors);
    if (nextErrors.customDocumentTitle) {
      focusField('customDocumentTitle');
      return;
    }
    if (nextErrors.customFile) {
      focusField('customFile');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('FileName', customDocumentTitle.trim());
      formData.append('FileAttachment', customFile);
      formData.append('IsShowToDMUT', 'true');

      if (customCategory?.value) {
        formData.append('DocumentUploadId', customCategory.value);
      }

      const response = await axios.post(
        `${api.API_URL}/procurement-document/custom-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response?.data?.data ?? response?.data;
      const newId = unwrapApiDocumentId(responseData);

      if (!newId) {
        // Response may not return id — sync from server list instead of Date.now()
        await fetchDocumentUpload(ids);
        notify.success('File uploaded successfully.');
        resetCustomModal();
        return;
      }

      const newDoc = {
        id: newId,
        documentName: customDocumentTitle.trim(),
        customFileName: customDocumentTitle.trim(),
        documentUploadId:
          responseData?.documentUploadId ??
          responseData?.DocumentUploadId ??
          customCategory?.value ??
          null,
        actualUrl:
          responseData?.fileUrl ??
          responseData?.FileUrl ??
          responseData?.link ??
          responseData?.Link ??
          null,
        updated: formatDate(new Date()),
      };
      setOtherDocuments(prev => [...prev, newDoc]);

      notify.success('File uploaded successfully.');
      resetCustomModal();
      await fetchDocumentUpload(ids);
    } catch (error) {
      notify.apiError(error, 'upload file');
    } finally {
      setUploading(false);
    }
  };

  const renderUploadModal = ({
    isOpen,
    toggle,
    title,
    description,
    onFileChange,
    fileInputRef,
    onUpload,
    uploadLabel = 'Upload File',
    uploading,
    fileError,
  }) => (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} className='border-0 pb-0'>
        <span className='modal-title fw-semibold mb-0'>{title}</span>
      </ModalHeader>
      <ModalBody className='pt-2'>
        <p className='text-muted small mb-3'>
          {description ||
            'Select a file to upload. The existing file will be replaced.'}
        </p>
        <hr className='text-muted opacity-25 mt-0 mb-4' />

        <RequiredNote />
        <FormGrid columns={1}>
          <FileField
            label='File'
            name='supportFile'
            required
            inputRef={fileInputRef}
            error={fileError}
            mode={uploading ? 'disabled' : 'editable'}
            onChange={e => onFileChange(e.target.files?.[0] || null)}
          />
        </FormGrid>

        <FormActions>
          <ActionButton
            variant='tertiary'
            icon='ri-close-line'
            onClick={toggle}
          >
            Close
          </ActionButton>
          <ActionButton
            variant='primary'
            onClick={onUpload}
            loading={uploading}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : uploadLabel}
          </ActionButton>
        </FormActions>
      </ModalBody>
    </Modal>
  );

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <div className='mb-4'>
            <span className='mb-1 fw-semibold'>Document Attachment</span>
            <p className='text-muted mb-0 fs-12'>
              Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
            </p>
          </div>
        </Col>
        <Col sm={12}>
          {!isReadOnly && (
            <div className='mb-4'>
              <div
                role='button'
                tabIndex={0}
                className='border rounded-3 p-3 d-flex align-items-center bg-white'
                style={{
                  cursor: 'pointer',
                  maxWidth: '350px',
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => setCustomUploadModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setCustomUploadModal(true);
                  }
                }}
              >
                <div
                  className='d-flex align-items-center justify-content-center rounded-circle me-3'
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#f8f9fa',
                    flexShrink: 0,
                  }}
                >
                  <i className='ri-upload-2-line fs-20 text-primary'></i>
                </div>

                <div className='flex-grow-1'>
                  <div className='fs-14 fw-semibold text-dark mb-0'>
                    Upload File
                  </div>
                  <div className='text-muted fs-12'>
                    Browse from your device
                  </div>
                </div>
              </div>
            </div>
          )}
        </Col>

        {/* Procurement Documents Section */}
        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>
              Procurement Documents
            </h6>
          </div>

          {!docsLoaded ? (
            <>
              <SkeletonCard lines={1} className='border mb-3' />
              <SkeletonCard lines={1} className='border mb-3' />
            </>
          ) : procurementDocuments.length === 0 ? (
            <EmptyState
              compact
              icon='ri-file-list-3-line'
              title='No procurement documents'
              emptyHint='The buyer has not shared any procurement documents for this tender yet.'
            />
          ) : (
            <div>
              {procurementDocuments.map(doc => (
                <div
                  key={doc.id}
                  className='border rounded p-3 mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'
                >
                  <div
                    className='d-flex align-items-center gap-3'
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className='bg-light rounded d-flex align-items-center justify-content-center flex-shrink-0'
                      style={{ width: '48px', height: '48px' }}
                    >
                      <i
                        className={`ri-file-text-line fs-24 ${
                          doc.hasCounter
                            ? 'text-warning'
                            : doc.isMasterDocument
                              ? 'text-success'
                              : 'text-primary'
                        }`}
                      />
                    </div>
                    <div>
                      <h6 className='fw-medium text-dark mb-1 text-break'>
                        {doc.documentName || doc.customFileName || '-'}
                      </h6>
                      <div className='d-flex flex-wrap align-items-center gap-2 small'>
                        <span
                          className={`badge ${
                            doc.hasCounter
                              ? 'bg-warning-subtle text-warning'
                              : doc.isMasterDocument
                                ? 'bg-success-subtle text-success'
                                : 'bg-primary-subtle text-primary'
                          }`}
                        >
                          {doc.hasCounter
                            ? 'Countered'
                            : doc.isMasterDocument
                              ? 'Master Document'
                              : 'Upload Custom'}
                        </span>
                        {doc.updated && (
                          <span className='text-muted'>
                            Updated: {doc.updated}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    <ActionButton
                      variant='secondary'
                      icon='ri-download-2-line'
                      onClick={() => handleDirectOpenNewTab(doc.actualUrl)}
                      disabled={!doc.actualUrl}
                    >
                      Download
                    </ActionButton>
                    <ActionButton
                      variant='secondary'
                      icon='ri-eye-line'
                      onClick={() => handleDirectOpenNewTab(doc.actualUrl)}
                      disabled={!doc.actualUrl}
                    >
                      View
                    </ActionButton>
                    {!isReadOnly && (
                      <ActionButton
                        variant='secondary'
                        onClick={() =>
                          setUploadModal({ isOpen: true, document: doc })
                        }
                      >
                        {doc.hasCounter ? 'Re-upload' : 'Counter'}
                      </ActionButton>
                    )}
                    {!isReadOnly && doc.hasCounter && (
                      <ActionButton
                        variant='destructive'
                        icon='ri-delete-bin-line'
                        confirm={false}
                        onClick={() =>
                          handleConfirmDelCounter(
                            doc.counterId,
                            doc.documentName || doc.customFileName
                          )
                        }
                      >
                        Delete
                      </ActionButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>

        {/* Other Documents Section */}
        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>Other Documents</h6>
          </div>

          {!docsLoaded ? (
            <SkeletonCard lines={1} className='border mb-3' />
          ) : otherDocuments.length === 0 ? (
            <EmptyState
              compact
              icon='ri-upload-cloud-2-line'
              title='No other documents uploaded'
              emptyHint={
                isReadOnly
                  ? 'No additional documents were uploaded with this offer.'
                  : 'Additional documents you upload with your offer will appear here.'
              }
              action={
                isReadOnly
                  ? null
                  : {
                      label: 'Upload file',
                      icon: 'ri-upload-2-line',
                      onClick: () => setCustomUploadModal(true),
                    }
              }
            />
          ) : (
            <div>
              {otherDocuments.map(data => (
                <div
                  key={data.id}
                  className='border rounded p-3 mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'
                >
                  <div
                    className='d-flex align-items-center gap-3'
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className='bg-light rounded d-flex align-items-center justify-content-center flex-shrink-0'
                      style={{ width: '48px', height: '48px' }}
                    >
                      <i className='ri-file-text-line text-primary fs-24' />
                    </div>
                    <div>
                      <h6 className='fw-medium text-dark mb-1 text-break'>
                        {data.documentName || data.customFileName || '-'}
                      </h6>
                      <div className='d-flex flex-wrap align-items-center gap-2 small'>
                        <span className='badge bg-primary-subtle text-primary'>
                          Upload Custom
                        </span>
                        {data.updated && (
                          <span className='text-muted'>
                            Updated: {data.updated}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    <ActionButton
                      variant='secondary'
                      icon='ri-download-2-line'
                      onClick={() => handleDirectOpenNewTab(data.actualUrl)}
                      disabled={!data.actualUrl}
                    >
                      Download
                    </ActionButton>
                    <ActionButton
                      variant='secondary'
                      icon='ri-eye-line'
                      onClick={() => handleDirectOpenNewTab(data.actualUrl)}
                      disabled={!data.actualUrl}
                    >
                      View
                    </ActionButton>
                    {!isReadOnly && (
                      <ActionButton
                        variant='destructive'
                        icon='ri-delete-bin-line'
                        confirm={false}
                        onClick={() =>
                          handleConfirmDelFileUpload(
                            data.id,
                            true,
                            data.documentName || data.customFileName
                          )
                        }
                      >
                        Delete
                      </ActionButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>

      {!isReadOnly && (
        <div className='req-actionbar flex-wrap d-print-none'>
          <ActionButton
            variant='secondary'
            icon='ri-draft-line'
            disabled={isExecutingOffer}
            onClick={handleExecuteDraft}
          >
            Save as Draft
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            disabled={isExecutingDraft}
            onClick={handleExecuteOffer}
          >
            Submit Offer
          </ActionButton>
        </div>
      )}

      {renderUploadModal({
        isOpen: uploadModal.isOpen,
        toggle: resetUploadModal,
        title: (() => {
          const docName =
            uploadModal.document?.documentName ||
            uploadModal.document?.customFileName ||
            'Document';
          if (!uploadModal.document) return 'Upload Document';
          return uploadModal.document.hasCounter
            ? `Re-upload: ${docName}`
            : `Counter: ${docName}`;
        })(),
        description: uploadModal.document?.hasCounter
          ? 'Select a new file to update the existing counter document.'
          : 'Upload a counter document for this procurement document.',
        onFileChange: file => {
          setUploadFile(file);
          setUploadFileError('');
        },
        fileError: uploadFileError,
        fileInputRef: uploadFileRef,
        onUpload: handleCounterDocument,
        uploadLabel: uploadModal.document?.hasCounter
          ? 'Re-upload File'
          : 'Upload Counter',
        uploading,
      })}

      <Modal isOpen={customUploadModal} toggle={resetCustomModal} centered>
        <ModalHeader toggle={resetCustomModal} className='border-0 pb-0'>
          <span className='modal-title fw-semibold mb-0'>
            Upload Custom Document
          </span>
        </ModalHeader>
        <ModalBody className='pt-2'>
          <p className='text-muted small mb-3'>
            Enter document title and select a file to upload.
          </p>
          <hr className='text-muted opacity-25 mt-0 mb-4' />

          <RequiredNote />
          <FormGrid columns={1}>
            <TextField
              label='Document title'
              name='customDocumentTitle'
              required
              placeholder='e.g. Technical specification'
              value={customDocumentTitle}
              error={customErrors.customDocumentTitle}
              onChange={e => {
                setCustomDocumentTitle(e.target.value);
                setCustomErrors(prev => ({
                  ...prev,
                  customDocumentTitle: undefined,
                }));
              }}
            />
            <FileField
              label='File'
              name='customFile'
              required
              inputRef={customFileRef}
              error={customErrors.customFile}
              mode={uploading ? 'disabled' : 'editable'}
              onChange={e => {
                setCustomFile(e.target.files?.[0] || null);
                setCustomErrors(prev => ({ ...prev, customFile: undefined }));
              }}
            />
          </FormGrid>

          <FormActions>
            <ActionButton
              variant='tertiary'
              icon='ri-close-line'
              onClick={resetCustomModal}
            >
              Close
            </ActionButton>
            <ActionButton
              variant='primary'
              onClick={handleCustomUpload}
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? (
                'Uploading...'
              ) : (
                <>
                  <i className='ri-upload-2-line align-middle me-2'></i>Upload
                  File
                </>
              )}
            </ActionButton>
          </FormActions>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default SupportDocumentTab;
